import {
  BASKET_PAYMENT_TYPE_SOURCE,
  BASKET_STATUS,
  PAYMENT_TYPE,
  RECEIPT_STATUS_TYPE,
} from '@/components/sales/checkout/consts'
import { isCompletedAdapterStatus } from './registration-state.ts'

export type CustomInvoiceGateInput = {
  country?: string | null
  verifactuEnabled?: boolean | null
  adapterStatus?: string | null
  dueInvoicingChangesEnabled?: boolean | null
}

type PaymentTypeRef = {
  code?: string | null
  source?: string | null
}

type PaymentRow = {
  id?: number | string | null
  payment_type_code?: string | null
  payment_type?: PaymentTypeRef | null
  status_code?: string | null
  short_status?: string | null
  status_type?: string | null
}

const PREPAYMENT_SUCCESS_STATUS_CODES = new Set([
  'B', // PREPAYMENT_SUCCESS
  'K', // PREPAYMENT_AUTHORISATION_SUCCESS
])

const PREPAYMENT_SUCCESS_SHORT_STATUSES = new Set([
  RECEIPT_STATUS_TYPE.PREPAYMENT,
  RECEIPT_STATUS_TYPE.AUTHORIZED,
])

type PaymentInfoTransaction = {
  id?: number | string | null
  payment_type_code?: string | null
  payment_type?: PaymentTypeRef | null
  status_code?: string | null
  short_status?: string | null
  status_type?: string | null
  payment_rows?: PaymentRow[] | null
}

type AppointmentPaymentDetailsInput = {
  transactionInfo?: AppointmentPaymentInput | null
  appointmentPayments?: AppointmentPaymentInput[] | null
}

type AppointmentPaymentInput = {
  id?: string | null
  paymentTypeCode?: string | null
  paymentType?: PaymentTypeRef | null
  transactionId?: number | string | null
  status?: { code?: string | null } | string | null
}

export function shouldDisableCustomInvoices ({
  country,
  verifactuEnabled,
  adapterStatus,
  dueInvoicingChangesEnabled,
}: CustomInvoiceGateInput): boolean {
  if (dueInvoicingChangesEnabled !== true) {
    return false
  }

  if (country !== 'es') {
    return false
  }

  if (verifactuEnabled !== true) {
    return false
  }

  return isCompletedAdapterStatus(adapterStatus)
}

function isSuccessfulLegacyPrepaymentStatus (
  statusCode?: string | null,
  shortStatus?: string | null,
  statusType?: string | null
): boolean {
  if (statusCode && PREPAYMENT_SUCCESS_STATUS_CODES.has(statusCode)) {
    return true
  }

  const status = shortStatus ?? statusType
  return Boolean(status && PREPAYMENT_SUCCESS_SHORT_STATUSES.has(status))
}

function isSuccessfulBasketPaymentStatus (statusCode?: string | null): boolean {
  return statusCode === BASKET_STATUS.SUCCESS
}

function isPrepaymentPaymentType (
  paymentTypeCode?: string | null,
  paymentType?: PaymentTypeRef | null
): boolean {
  return paymentTypeCode === PAYMENT_TYPE.PREPAYMENT
    || paymentType?.source === BASKET_PAYMENT_TYPE_SOURCE.PREPAYMENT
}

function resolveBasketPaymentStatusCode (status?: { code?: string | null } | string | null): string | null {
  if (typeof status === 'string') {
    return status
  }

  return status?.code ?? null
}

function isSuccessfulPrepaymentTransaction (transaction?: PaymentInfoTransaction | null): boolean {
  if (!transaction?.id || !isPrepaymentPaymentType(transaction.payment_type_code, transaction.payment_type)) {
    return false
  }

  return isSuccessfulLegacyPrepaymentStatus(
    transaction.status_code,
    transaction.short_status,
    transaction.status_type
  )
}

function hasSuccessfulPrepaymentInPaymentInfo (paymentInfo?: {
  payable?: boolean
  transaction_info?: PaymentInfoTransaction | null
} | null): boolean {
  const transactionInfo = paymentInfo?.transaction_info

  if (isSuccessfulPrepaymentTransaction(transactionInfo)) {
    return true
  }

  const paymentRows = transactionInfo?.payment_rows ?? []
  return paymentRows.some((row) => (
    Boolean(row.id)
    && isPrepaymentPaymentType(row.payment_type_code, row.payment_type)
    && isSuccessfulLegacyPrepaymentStatus(row.status_code, row.short_status, row.status_type)
  ))
}

function isSuccessfulAppointmentPrepaymentPayment (payment?: AppointmentPaymentInput | null): boolean {
  const paymentId = payment?.transactionId ?? payment?.id
  if (!paymentId || !isPrepaymentPaymentType(payment?.paymentTypeCode, payment?.paymentType)) {
    return false
  }

  return isSuccessfulBasketPaymentStatus(resolveBasketPaymentStatusCode(payment?.status))
}

function hasSuccessfulPrepaymentInAppointmentPaymentDetails (
  appointmentPaymentDetails?: AppointmentPaymentDetailsInput | null
): boolean {
  if (!appointmentPaymentDetails) {
    return false
  }

  const appointmentPayments = appointmentPaymentDetails.appointmentPayments ?? []
  if (appointmentPayments.some(isSuccessfulAppointmentPrepaymentPayment)) {
    return true
  }

  return isSuccessfulAppointmentPrepaymentPayment(appointmentPaymentDetails.transactionInfo)
}

export function hasSuccessfulAppointmentPrepayment ({
  paymentInfo,
  appointmentPaymentDetails,
  useBasketPaymentDetails,
}: {
  paymentInfo?: {
    payable?: boolean
    transaction_info?: PaymentInfoTransaction | null
  } | null
  appointmentPaymentDetails?: AppointmentPaymentDetailsInput | null
  useBasketPaymentDetails?: boolean
} = {}): boolean {
  if (useBasketPaymentDetails) {
    if (hasSuccessfulPrepaymentInAppointmentPaymentDetails(appointmentPaymentDetails)) {
      return true
    }

    return hasSuccessfulPrepaymentInPaymentInfo(paymentInfo)
  }

  return hasSuccessfulPrepaymentInPaymentInfo(paymentInfo)
}

export function shouldHidePrepaidServiceRemoval ({
  verifactuGateActive,
  paymentInfo,
  appointmentPaymentDetails,
  useBasketPaymentDetails,
}: {
  verifactuGateActive: boolean
  paymentInfo?: {
    payable?: boolean
    transaction_info?: PaymentInfoTransaction | null
  } | null
  appointmentPaymentDetails?: AppointmentPaymentDetailsInput | null
  useBasketPaymentDetails?: boolean
}): boolean {
  if (!verifactuGateActive) {
    return false
  }

  return hasSuccessfulAppointmentPrepayment({
    paymentInfo,
    appointmentPaymentDetails,
    useBasketPaymentDetails,
  })
}
